'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import posthog from 'posthog-js'
import { ArrowUpRight, Film, Smartphone, Users } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import type { Tournament } from '@/lib/tournaments'

const IOS_APP_URL = 'https://apps.apple.com/us/app/gamerplug/id6752116866'
const ANDROID_APP_URL =
  'https://play.google.com/store/apps/details?id=com.ionpetro.gamerplug&pcampaignid=web_share'

type Step = 'add_clip' | 'find_teammates' | 'get_app'

function detectDevice(): 'ios' | 'android' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop'
  const ua = window.navigator.userAgent.toLowerCase()
  if (/iphone|ipad|ipod/.test(ua)) return 'ios'
  if (/android/.test(ua)) return 'android'
  return 'desktop'
}

/**
 * Shown right after a successful registration. Tournament signups mostly never
 * reach the deck (no clip means nobody can see them), so point them at the
 * steps that make them findable: add a clip, swipe for teammates, get the app.
 */
export function TournamentNextSteps({
  t,
  userId,
  gamertag,
  locale,
}: {
  t: Tournament
  userId: string
  gamertag: string | null
  locale: string
}) {
  const [hasClips, setHasClips] = useState<boolean | null>(null)
  const [device, setDevice] = useState<'ios' | 'android' | 'desktop'>('desktop')
  const shownRef = useRef(false)

  useEffect(() => {
    setDevice(detectDevice())
    let cancelled = false
    supabase
      .from('users')
      .select('has_clips')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setHasClips(Boolean(data?.has_clips))
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  const steps: Step[] =
    hasClips === null
      ? []
      : [...(hasClips || !gamertag ? [] : ['add_clip' as const]), 'find_teammates', 'get_app']

  useEffect(() => {
    if (shownRef.current || steps.length === 0) return
    shownRef.current = true
    for (const step of steps) {
      posthog.capture('tournament_next_step_shown', {
        step,
        platform: 'web',
        tournament_id: t.id,
      })
    }
  }, [steps, t.id])

  if (steps.length === 0) return null

  const track = (step: Step) =>
    posthog.capture('tournament_next_step_clicked', {
      step,
      platform: 'web',
      tournament_id: t.id,
    })

  const rowClass =
    'flex items-center gap-3 rounded-xl bg-white/[0.03] px-3.5 py-3 text-sm transition-colors hover:bg-white/10'
  const iconClass =
    'flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary'

  const storeUrl = device === 'ios' ? IOS_APP_URL : device === 'android' ? ANDROID_APP_URL : null

  return (
    <div className="mt-3 w-full rounded-2xl border border-border bg-card/60 p-4 animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
      <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        You&apos;re in. Next steps
      </p>
      <div className="flex flex-col gap-2">
        {steps.includes('add_clip') && gamertag && (
          <Link
            href={`/${locale}/app/profile/${encodeURIComponent(gamertag)}`}
            onClick={() => track('add_clip')}
            className={rowClass}
          >
            <span className={iconClass}>
              <Film className="size-4" />
            </span>
            <span className="min-w-0">
              <span className="block font-medium">Add a clip so teammates can find you</span>
              <span className="block text-xs text-muted-foreground">
                Players without a clip don&apos;t show up when others swipe.
              </span>
            </span>
          </Link>
        )}

        <Link
          href={`/${locale}/app/explore`}
          onClick={() => track('find_teammates')}
          className={rowClass}
        >
          <span className={iconClass}>
            <Users className="size-4" />
          </span>
          <span className="min-w-0">
            <span className="block font-medium">Find teammates for {t.title}</span>
            <span className="block text-xs text-muted-foreground">
              Swipe through players and match before the event.
            </span>
          </span>
        </Link>

        <div className={rowClass}>
          <span className={iconClass}>
            <Smartphone className="size-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-medium">Get the app</span>
            <span className="block text-xs text-muted-foreground">
              Get notified when you match or get a message.
            </span>
          </span>
          <span className="flex shrink-0 gap-2">
            {(storeUrl ? [storeUrl] : [IOS_APP_URL, ANDROID_APP_URL]).map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track('get_app')}
                className="inline-flex items-center gap-1 rounded-lg bg-white/10 px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-white/20"
              >
                {url === IOS_APP_URL ? 'iOS' : 'Android'}
                <ArrowUpRight className="size-3.5" />
              </a>
            ))}
          </span>
        </div>
      </div>
    </div>
  )
}
